import { FileDirectory } from './../../shared/models/file-directory';
import { FileAssignComponentsRequest } from './../../shared/models/file-assign-components-request';
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as actions from '../../files-store';
import { getSelectedProperty, getSelectedFiscalYear } from '../../../property/property-store/property.selectors';
import { AppState } from '../../../../app.reducers';
import { Property } from '../../../property/shared/models';
import { SubSink } from 'subsink';
import { combineLatest } from 'rxjs';
import { FilterSettings, FiscalYear, ModalComponent } from 'app/internal/shared';
import { filter } from 'rxjs/operators';
import { LoadFilesRequest, PropertyFile, FolderActionRequest, FileActionRequest, FileBulkActionRequest } from '../../shared/models';
import { getBulkActionStatus, getDirectoryInfo, getFolderStructure } from '../../files-store/files.selectors';
import { ActivatedRoute } from '@angular/router';
import { AddFilesModalComponent } from '../../components/add-files-modal';


@Component({
  templateUrl: './file-library.component.html',
  styleUrls: ['./file-library.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FileLibraryComponent implements OnInit, OnDestroy {
  @ViewChild('addFiles', { static: true }) public addFilesModal: AddFilesModalComponent;
  @ViewChild('createFolder', { static: true }) public createFolderModal: ModalComponent;

  property$: Observable<Property>;
  selectedFiscalYear$: Observable<FiscalYear>;
  directoryInfo$: Observable<FileDirectory>;
  folderStructure$: Observable<FileDirectory>;
  getBulkActionStatus$: Observable<string>;

  selectedPropertyId: string;
  selectedFiscalYear: FiscalYear;
  selectedFolderId: string;
  fileToView: PropertyFile;
  errorMsg = '';
  createFolderName: string;

  showFilter = false;
  filterSettings = new FilterSettings('', null);

  isBulkAssignPending = false;
  isBulkDeletePending = false;
  isBulkDownloadPending = false;
  isBulkReportSettingsUpdatePending = false;

  private subs = new SubSink();

  @ViewChild('errorModal', { static: true }) public errorModal: ModalComponent;

  constructor(private store: Store<AppState>, private ref: ChangeDetectorRef, private route: ActivatedRoute) {
    this.property$ = this.store.pipe(select(getSelectedProperty));
    this.selectedFiscalYear$ = this.store.pipe(select(getSelectedFiscalYear));
    this.directoryInfo$ = this.store.pipe(select(getDirectoryInfo));
    this.folderStructure$ = this.store.pipe(select(getFolderStructure));
    this.getBulkActionStatus$ = this.store.pipe(select(getBulkActionStatus));
  }

  ngOnInit() {
    this.subs.sink = combineLatest([this.selectedFiscalYear$, this.property$])
      .pipe(filter(([fy, p]) => fy != null && p != null && !fy.requireSetup ))
      .subscribe(([fiscalYear, property]) => {
        if (this.selectedPropertyId != null && this.selectedPropertyId !== property.propertyId) {
          // property changed, no need to load this information because we'll be redirected to the dashboard.
          return;
        }

        this.selectedPropertyId = property.propertyId;
        this.selectedFiscalYear = fiscalYear;

        this.getFiles();
        this.store.dispatch(new actions.LoadPropertyFoldersAction(this.selectedPropertyId));

        this.filterSettings = new FilterSettings(this.selectedPropertyId, null);
      });

    this.subs.sink = this.getBulkActionStatus$
      .subscribe((status) => {
        // bulk action has completed, reload files
        if (status === 'complete') {
          if (!this.isBulkDownloadPending) {
            this.getFiles();
          }
          this.clearBulkActionFlags();
        } else if (status === 'failed') {
          if (this.isBulkAssignPending) {
            this.errorMsg = 'Uh oh. An error occured while assigning the selected files to a component.';
          } else if (this.isBulkDeletePending) {
            this.errorMsg = 'Uh oh. An error occured while deleting the selected files. Please try again.';
          } else if (this.isBulkDownloadPending) {
            this.errorMsg = 'Uh oh. An error occured while downloading the selected files. ' +
                        'If a downloading a large number of files, try selecting a smaller set and try again.';
          } else {
            this.errorMsg = 'Uh oh. An error occured. Please try again.';
          }

          this.ref.detectChanges();
          this.errorModal.show();
          this.clearBulkActionFlags();
        }
      });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.store.dispatch(new actions.ClearPropertyFiles());
  }

  private clearBulkActionFlags() {
    this.isBulkAssignPending = false;
    this.isBulkDeletePending = false;
    this.isBulkDownloadPending = false;
    this.isBulkReportSettingsUpdatePending = false;
  }

  getFiles() {
    const request = new LoadFilesRequest(this.selectedPropertyId, this.selectedFolderId, this.filterSettings.filter);
    this.store.dispatch(new actions.LoadPropertyFilesAction(request));
  }

  isDirectoryEmpty(directory: FileDirectory) {
    if (directory == null ) { return true; }

    if ((directory.files == null || directory.files.length === 0) && (directory.folders == null || directory.folders.length === 0)) {
      return true;
    }

    return false;
  }

  hasSeachFilters() {
    return this.filterSettings != null
            && (this.filterSettings.filter != null && this.filterSettings.filter.length > 0);
  }

  showUploadFileModal() {
    this.addFilesModal.showModal();
  }

  onUploadFilesComplete(filesAdded: boolean) {
    // reload this folder if files were added
    if (filesAdded) {
      this.getFiles();
    }
  }

  showAddFolderModal() {
    this.createFolderName = '';
    this.createFolderModal.show();
  }

  onCreateFolder() {
    const request = new FolderActionRequest(this.selectedPropertyId, null, this.createFolderName, this.selectedFolderId);
    this.store.dispatch(new actions.CreatePropertyFolderAction(request));
  }

  onFolderSelected(folderId: string) {
    this.selectedFolderId = folderId;
    this.getFiles();
    this.route.queryParams['folderId'] = folderId;
  }

  onFolderAdded(request: FolderActionRequest) {
    this.store.dispatch(new actions.CreatePropertyFolderAction(request));
  }

  onFolderRenamed(request: FolderActionRequest) {
    this.store.dispatch(new actions.RenamePropertyFolderAction(request));
  }

  onFolderMoved(request: FolderActionRequest) {
    this.store.dispatch(new actions.MovePropertyFolderAction(request));
  }

  onFolderDeleted(request: FolderActionRequest) {
    this.store.dispatch(new actions.DeletePropertyFolderAction(request));
  }

  onFileRenamed(request: FileActionRequest) {
    this.store.dispatch(new actions.RenamePropertyFileAction(request));
  }

  onFileMoved(request: FileActionRequest) {
    this.store.dispatch(new actions.MovePropertyFileAction(request));
  }

  onFileDeleted(request: FileActionRequest) {
    this.store.dispatch(new actions.DeletePropertyFileAction(request));
  }

  onDownloadFile(request: FileActionRequest) {
    this.store.dispatch(new actions.DownloadPropertyFileAction(request));
  }

  onAssignComponents(request: FileAssignComponentsRequest) {
    this.isBulkAssignPending = true;
    this.store.dispatch(new actions.AssignComponentsPropertyFileAction(request));
  }

  onMoveBulk(request: FileBulkActionRequest) {
    this.store.dispatch(new actions.BulkMovePropertyFilesAction(request));
  }

  onDownloadBulk(request: FileBulkActionRequest) {
    this.store.dispatch(new actions.BulkDownloadPropertyFilesAction(request));
  }

  onDeleteBulk(request: FileBulkActionRequest) {
    this.store.dispatch(new actions.BulkDeletePropertyFilesAction(request));
  }


  /************* FILTER METHODS ****************/
  onToggleFilter(toggleOn = null) {
    // If toggleOn is not null, use it. Else simple toggle 'showFilter'
    if (toggleOn != null) {
      this.showFilter = toggleOn;
      return;
    }
    this.showFilter = !this.showFilter;
  }

  onHideFilter() {
    this.onToggleFilter();
  }

  onSearchFiles(filters: FilterSettings) {
    this.filterSettings = filters;
    this.getFiles();
  }
}
